import { useCallback, useState } from 'react';

/**
 * useDirtyConfirm — wraps an `onClose` handler with "are you sure?" semantics.
 *
 * Pattern:
 *   const isDirty = formText.trim().length > 0;
 *   const { showConfirm, requestClose, confirmDiscard, cancelDiscard } =
 *     useDirtyConfirm({ onClose, isDirty });
 *
 *   // Wire requestClose into X button + Esc key + outside-click handlers.
 *   // When showConfirm === true, render an inline <InlineConfirmCloseBar/>.
 *
 * Why we don't just stack a second modal:
 *   stacking modals creates focus + a11y chaos. We just spent a session preventing
 *   it. Instead, modals render a small confirm strip *inside* their own body when
 *   ``showConfirm`` is true.
 */
export default function useDirtyConfirm({ onClose, isDirty }) {
  const [showConfirm, setShowConfirm] = useState(false);

  const requestClose = useCallback(() => {
    if (!isDirty) {
      setShowConfirm(false);
      if (typeof onClose === 'function') onClose();
      return;
    }
    setShowConfirm(true);
  }, [isDirty, onClose]);

  const confirmDiscard = useCallback(() => {
    setShowConfirm(false);
    if (typeof onClose === 'function') onClose();
  }, [onClose]);

  const cancelDiscard = useCallback(() => {
    setShowConfirm(false);
  }, []);

  return { showConfirm, requestClose, confirmDiscard, cancelDiscard };
}
